import axios from 'axios';
import { isServer, settings } from '../settings';
import { mutateDate, formatDatePagination } from '../lib/helpers';
import { getAccessToken } from 'services/auth';

const dumpertService = (accessToken, nsfwActive) => {
  const authHeader = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

  // exclude staging and localhost, because the server doesn't support this header
  const cacheHeader = settings.API_SERVICE_URL_POST.includes('staging')
    ? {}
    : { 'Cache-Control': 'no-cache' };

  const nsfwHeader = {
    'X-Dumpert-NSFW': nsfwActive ? '1' : '0',
  };

  return axios.create({
    baseURL: (isServer && settings.API_SERVICE_INTERNAL_URL_POST) || settings.API_SERVICE_URL_POST,
    timeout: isServer ? 5000 : 10000,
    headers: {
      ...cacheHeader,
      ...nsfwHeader,
      ...authHeader,
    },
  });
};

export const getItemsList = async ({
  list = 'latest',
  filter,
  tag,
  period,
  sorting = 'date',
  page = 0,
  itemId,
  date,
  nsfwActive = false,
}) => {
  const setFilter = filter ? `${filter}/` : '';
  const setPeriod = period ? `${period.replace('dag', 'day')}/` : '';
  const currentDate = new Date();
  const searchUrl = tag === 'dumperttv' ? 'dumperttv' : `${list}/${encodeURIComponent(tag)}`;

  const urlPath = () => {
    switch (list) {
      case 'latest':
        return date
          ? `latest/date/${date.split('-')[2]}-${date.split('-')[1]}-${date.split('-')[0]}/`
          : `${setFilter}${list}/${setPeriod}/${page}/`;
      case 'toppers': {
        if (period) {
          return `${setFilter}top5/${period}/${formatDatePagination(mutateDate(currentDate, period, -page), period)}/`;
        } else {
          return `${setFilter}${list}/${page}/`;
        }
      }
      case 'related':
        return `${setFilter}${list}/${itemId}/`;
      case 'floppers':
        return `${setFilter}${list}/${page}/`;

      // https://api-live.dumpert.nl/mobile_api/json/ui/#!/Legacy/search_term_page_get
      // https://api-live.dumpert.nl/mobile_api/json/search/hallo/0?order=kudos-
      case 'search':
        return `${searchUrl}/${page}/?order=${sorting}&media_type=${filter || 'all'}`;
      case 'tag':
        return `${encodeURIComponent(tag)}/${page}/?order=${sorting}`;
      default:
        throw new Error(`Unknown list: ${list}`);
    }
  };

  const url = new URL(`https://dumpert.nl/${urlPath().replace('//', '/')}`);
  if (!isServer) {
    url.searchParams.append('app', window.location.host);
  }
  return dumpertService(await getAccessToken(), nsfwActive).get(url.pathname + url.search);
};

export const getItemDetails = async ({ itemId }) => {
  return dumpertService(await getAccessToken()).get(`/info/${itemId}/`);
};

export const getRandomTopperFromPast = async ({ year, month, day }) => {
  return dumpertService(await getAccessToken()).get(`/video/top5/dag/${year}-${month}-${day}/`);
};

export const uploadFiles = (data, onUploadProgress) => {
  return axios.post(`${settings.API_SERVICE_URL_UPLOAD}/?JSON`, data, {
    onUploadProgress: (progressEvent) => {
      if (onUploadProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onUploadProgress(percentCompleted);
      }
    },
  });
};

export const kudoItem = async (itemId, direction) => {
  return dumpertService(await getAccessToken()).get(`/rating/${itemId}/${direction}`);
};

export const increaseView = async (itemId) => {
  return dumpertService(await getAccessToken()).get(`/viewed/${itemId}`);
};

export const submitItemReport = async (itemId, data) => {
  return dumpertService(await getAccessToken()).put(`/report/${itemId}`, data);
};

export const submitStemForm = async (itemId, data) => {
  return dumpertService(await getAccessToken()).post('/stem/topzoveel', data);
};
